import { consts } from '@allesauto/utils/consts';
import { ROUTES } from '@allesauto/utils/routes';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  IndicataResponse,
  NextStep,
  PrevStep,
  Relation,
} from '@/global/tradein/indicata.interface';
import { RootState } from '@/store/redux';

export type Option = {
  value: string | number;
  label: string | number;
} | null;

export type FormItem = {
  selectedOption?: Option;
  disabled: boolean;
  options: NextStep[];
  hidden?: boolean;
  error?: boolean;
  required?: boolean;
};

export type RelName =
  | Relation.Make
  | Relation.Model
  | Relation.Colors
  | Relation.RegMonth
  | Relation.RegDate
  | Relation.Body
  | Relation.FaceLift
  | Relation.Seats
  | Relation.Engine
  | Relation.WheelDrive
  | Relation.Transmission
  | Relation.Trim
  | Relation.BodyHeight
  | Relation.BodyLength
  | Relation.Weight;

export type FormState = {
  [value in RelName]: FormItem;
};

// The relation order matters (the hierarchy is deduced by it)
export const initialState: FormState = {
  make: {
    selectedOption: null,
    disabled: false,
    options: [],
    required: true,
  },
  model: {
    selectedOption: null,
    disabled: true,
    options: [],
  },
  colors: {
    selectedOption: null,
    disabled: true,
    options: [],
    required: true,
  },
  regmonth: {
    selectedOption: null,
    disabled: true,
    options: [],
  },
  regdate: {
    selectedOption: null,
    disabled: true,
    options: [],
  },
  body: {
    selectedOption: null,
    disabled: true,
    options: [],
    hidden: true,
  },
  facelift: {
    selectedOption: null,
    disabled: true,
    options: [],
    hidden: true,
  },
  seats: {
    selectedOption: null,
    disabled: true,
    options: [],
    hidden: true,
  },
  bodyHeight: {
    selectedOption: null,
    disabled: true,
    options: [],
    hidden: true,
  },
  bodyLength: {
    selectedOption: null,
    disabled: true,
    options: [],
    hidden: true,
  },
  weight: {
    selectedOption: null,
    disabled: true,
    options: [],
    hidden: true,
  },
  engine: {
    selectedOption: null,
    disabled: true,
    options: [],
    hidden: true,
  },
  wheeldrive: {
    selectedOption: null,
    disabled: true,
    options: [],
    hidden: true,
  },
  transmission: {
    selectedOption: null,
    disabled: true,
    options: [],
    hidden: true,
  },
  trim: {
    selectedOption: null,
    disabled: true,
    options: [],
    hidden: true,
  },
};

export const mockMonths: NextStep[] = (() => {
  return Array.from(Array(12).keys())
    .map((month) => `${month < 9 ? '0' : ''}${++month}`)
    .map((month) => ({
      name: month,
      images: [],
      description: [],
      href: '',
      type: '',
      rel: Relation.RegMonth,
      templated: false,
    }));
})();

export const getMockYears = (startYear: number): NextStep[] => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const years = [];

  while (startYear <= currentYear) {
    years.push(startYear);
    startYear++;
  }

  return years.reverse().map((year) => ({
    name: year.toString(),
    images: [],
    description: [],
    href: '',
    type: '',
    rel: Relation.RegDate,
    templated: false,
  }));
};

export function getMockedColors(link?: string): NextStep[] {
  return consts.COLOR_OPTIONS_INDICATA.map((color) => {
    return {
      summary: color,
      name: color,
      description: [],
      images: [],
      type: 'allesauto/static',
      href: link ? String(link) : '',
      rel: Relation.Colors,
      templated: false,
    };
  });
}

export function isGarageUrl(url: string): boolean {
  return (
    url.includes(ROUTES.GARAGE_ADD.paths.de) ||
    url.includes(ROUTES.GARAGE_ADD.paths.en)
  );
}

export function translateValues(value: string): string {
  switch (value) {
    case 'Beige':
      return 'BEIGE';
    case 'Schwarz':
      return 'BLACK';
    case 'Blau':
      return 'BLUE';
    case 'Braun':
      return 'BROWN';
    case 'Gold':
      return 'GOLD';
    case 'Grau':
      return 'GREY';
    case 'Grün':
      return 'GREEN';
    case 'Orange':
      return 'ORANGE';
    case 'Gelb':
      return 'YELLOW';
    case 'Silber':
      return 'SILVER';
    case 'Weiß':
      return 'WHITE';
    case 'Rot':
      return 'RED';
    case 'Pink':
      return 'PINK';
    case 'Lila':
      return 'PURPLE';
    default:
      return '';
  }
}

// Returns disabled form item object with a fixed label (used for skipped steps)
export const createSkippedStepObj = (
  prevStep: PrevStep,
  relationName: RelName,
  oldOption?: Option,
  options: NextStep[] = [],
  shouldPersist = false
): FormItem => {
  const isRegistration =
    relationName === Relation.RegDate || relationName === Relation.RegMonth;

  const label =
    shouldPersist && isRegistration && oldOption && oldOption?.label
      ? oldOption?.label
      : prevStep.summary || prevStep.name;

  return {
    hidden: false,
    options,
    selectedOption: {
      label,
      value: '',
    },
    disabled: !isRegistration,
    required: isRegistration,
  };
};

// Returns form state with all relations that are lower in hierarchy than the given relation
export const getLowerHierarchyRelations = (
  relation: RelName
): Partial<FormState> => {
  const partialInitialState: Partial<FormState> = {};
  const orderedSequenceNames = Object.keys(initialState);
  const indexOfRelation = orderedSequenceNames.indexOf(relation);
  const lowerHierarchyRelationNames = orderedSequenceNames.slice(
    indexOfRelation + 1
  ) as RelName[];

  lowerHierarchyRelationNames.forEach((relName) => {
    partialInitialState[relName] = initialState[relName];
  });

  return partialInitialState;
};

/**
 * Reflects Indicata sequence API data and current form state
 */
const slice = createSlice({
  name: 'tradein/sequence',
  initialState,
  reducers: {
    // Update Indicata API sequence data and form state by next step
    populateSeqForm(state, action: PayloadAction<IndicataResponse>) {
      const response = action.payload;
      const relation: Relation = response?.nextStep[0]?.rel;
      const partialState: Partial<FormState> = {};
      const isRequired = !response.valuation;

      // Iterate all relations in order to find skipped steps
      // Merge skipped steps into partialState as disabled form fields
      for (const relationName in initialState) {
        const rel = relationName as RelName;

        if (response[rel] && !state[rel].selectedOption) {
          const skippedStep = response[rel]!;
          partialState[rel] = createSkippedStepObj(skippedStep, rel);
        }
      }

      return {
        ...state,
        ...partialState,
        [relation]: {
          disabled: false,
          options: action.payload?.nextStep,
          selectedOption: null,
          hidden: false,
          required: isRequired,
        },
      };
    },
    // Reset sequence form to initial
    resetSeqForm() {
      return initialState;
    },
    // Select an item for the given relation
    selectSeqFormOption(
      state,
      action: PayloadAction<{
        relationName: RelName;
        selectedOption: Option;
      }>
    ) {
      const relation: RelName = action.payload?.relationName;
      const option: Option = action.payload?.selectedOption;
      const partialInitialState = getLowerHierarchyRelations(relation);

      return {
        ...state,
        ...partialInitialState,
        [relation]: {
          disabled: state[relation].disabled,
          options: state[relation].options,
          selectedOption: option,
          hidden: state[relation].hidden,
          required: state[relation].required,
        },
      };
    },
    // Deselect (reset to null) the item for the given relation
    deselectSeqFormOption(
      state,
      action: PayloadAction<{ relationName: RelName }>
    ) {
      const relation: RelName = action.payload?.relationName;
      const partialInitialState = getLowerHierarchyRelations(relation);

      return {
        ...state,
        ...partialInitialState,
        [relation]: {
          disabled: state[relation].disabled,
          options: state[relation].options,
          selectedOption: null,
          hidden: state[relation].hidden,
          required: state[relation].required,
        },
      };
    },
    setRequiredSeqFormFields(state) {
      const partialState: Partial<FormState> = {};

      // Iterate all relations in order to find missing user inputs
      for (const relationName in initialState) {
        const rel = relationName as RelName;

        if (
          !state[rel].hidden &&
          state[rel].required &&
          !state[rel].selectedOption
        ) {
          partialState[rel] = {
            disabled: state[rel].disabled,
            options: state[rel].options,
            selectedOption: state[rel].selectedOption,
            hidden: state[rel].hidden,
            required: state[rel].required,
            error: true,
          };
        }
      }

      return {
        ...state,
        ...partialState,
      };
    },
  },
});

export const {
  populateSeqForm,
  resetSeqForm,
  selectSeqFormOption,
  deselectSeqFormOption,
  setRequiredSeqFormFields,
} = slice.actions;

export const getTradeInSequenceState = (state: RootState): FormState =>
  state.pages.tradeIn.sequence;

export default slice.reducer;
